import { ChevronDown, ChevronLeft, Plus, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router-dom';
import { useCreateQuestionnaire } from '../../hooks/useCreateQuestionnaire';
import { useInitialKeys } from '../../hooks/useInitialKeys';
import { useQuestionTypes } from '../../hooks/useQuestionTypes';

import {
  AddQuestionButton,
  BackButton,
  Button,
  Checkbox,
  Container,
  ContentWrapper,
  FormContainer,
  FormSection,
  Header,
  HeaderTitle,
  Input,
  QuestionItem,
  QuestionsContainer,
  Select,
  SelectArrow,
  SelectContainer,
  Textarea,
  Title,
  ToggleContainer,
  ToggleLabel,
} from './CreateQuestionnaire.Styles';

/**
 * @module CreateQuestionnairePage
 * @description Component for creating new questionnaires in the Mental Health Portal application.
 * Allows users to define questionnaire metadata, add multiple questions, and configure question-specific settings.
 *
 * @requires react
 * @requires react-i18next
 * @requires react-oidc-context
 * @requires react-router-dom
 * @requires lucide-react
 * @requires ../../hooks/useCreateQuestionnaire
 * @requires ../../hooks/useInitialKeys
 * @requires ../../hooks/useQuestionTypes
 * @requires ./CreateQuestionnaire.Styles
 */
export default function CreateQuestionnairePage() {
  /** Translation hook for internationalization */
  const { t } = useTranslation();
  /** Navigation hook for routing */
  const navigate = useNavigate();

  /** @state {string} name - Questionnaire name */
  const [name, setName] = useState('');
  /** @state {string} toBeAppliedAt - When the questionnaire should be applied (initial, daily, weekly) */
  const [toBeAppliedAt, setToBeAppliedAt] = useState('');
  /** @state {string} category - Questionnaire category (personality, mood, anxiety, etc.) */
  const [category, setCategory] = useState('');
  /** @state {string} correctionType - Algorithm used for scoring (Big Five, Oldenburg, Balance Scale) */
  const [correctionType, setCorrectionType] = useState('');
  /** @state {string} introduction - Introductory text for the questionnaire */
  const [introduction, setIntroduction] = useState('');
  /** @state {string} questionTypeId - Selected question type ID */
  const [questionTypeId, setQuestionTypeId] = useState('');
  /** @state {Array<string>} questions - List of question texts */
  const [questions, setQuestions] = useState(['']);
  /** @state {Array<number>} keyIds - List of key IDs associated with each question */
  const [keyIds, setKeyIds] = useState([1]);
  /** @state {Array<boolean>} selectedQuestions - Boolean flags for inverting answers for each question */
  const [selectedQuestions, setSelectedQuestions] = useState([false]);
  /** Authentication hook for user management */
  const auth = useAuth();

  /** Custom hook for fetching question types */
  const { questionTypes, loading, error } = useQuestionTypes();
  /** Custom hook for fetching available keys for question categorization */
  const {
    initialKeys,
    loading: keysLoading,
    error: keysError,
  } = useInitialKeys();
  /** Custom hook for questionnaire creation API calls */
  const { createQuestionnaire, loading: saving } = useCreateQuestionnaire();

  /** User ID from authentication */
  const userId = auth.user?.profile?.sub;

  /**
   * @const {Object} friendlyLabels - Human-readable labels for question types
   * @property {string} '1-5' - Scale of 1 to 5
   * @property {string} '0-6' - Scale of 0 to 6
   * @property {string} 'yes-or-no' - Yes or No options
   */
  const friendlyLabels = {
    '1-5': `${t('Scale of 1 to 5')}`,
    '0-6': `${t('Scale of 0 to 6')}`,
    'yes-or-no': `${t('Yes or No')}`,
  };

  /**
   * @const {Array<Object>} keyOptions - Formatted options for key selection dropdowns
   * @property {number} id - Key ID
   * @property {string} label - Key name
   */
  const keyOptions = initialKeys.map((key) => ({
    id: key.id,
    label: key.name,
  }));

  /**
   * Navigates back to the questionnaires list page
   * @function handleBack
   * @returns {void}
   */
  const handleBack = () => {
    navigate('/questionnaires');
  };

  /**
   * Toggles the "invert answer" flag for a question
   * @function toggleQuestion
   * @param {number} index - Index of the question to toggle
   * @returns {void}
   */
  const toggleQuestion = (index) => {
    const updated = [...selectedQuestions];
    updated[index] = !updated[index];
    setSelectedQuestions(updated);
  };

  /**
   * Updates the text of a specific question
   * @function handleQuestionChange
   * @param {number} index - Index of the question to update
   * @param {string} value - New question text
   * @returns {void}
   */
  const handleQuestionChange = (index, value) => {
    const updated = [...questions];
    updated[index] = value;
    setQuestions(updated);
  };

  /**
   * Adds a new empty question to the list with default values
   * @function addQuestion
   * @returns {void}
   */
  const addQuestion = () => {
    setQuestions((prev) => [...prev, '']);
    setKeyIds((prev) => [...prev, 1]);
    setSelectedQuestions((prev) => [...prev, false]);
  };

  /**
   * Removes a question from the list
   * @function removeQuestion
   * @param {number} indexToRemove - Index of the question to remove
   * @returns {void}
   */
  const removeQuestion = (indexToRemove) => {
    setQuestions((prev) => prev.filter((_, i) => i !== indexToRemove));
    setKeyIds((prev) => prev.filter((_, i) => i !== indexToRemove));
    setSelectedQuestions((prev) => prev.filter((_, i) => i !== indexToRemove));
  };

  /**
   * Updates the key ID for a specific question
   * @function handleKeyChange
   * @param {number} index - Index of the question to update
   * @param {string|number} value - New key ID
   * @returns {void}
   */
  const handleKeyChange = (index, value) => {
    const updated = [...keyIds];
    updated[index] = Number(value);
    setKeyIds(updated);
  };

  /**
   * Validates form data and submits the questionnaire
   * @function handleNext
   * @async
   * @returns {Promise<void>}
   * @throws {Error} If questionnaire creation fails
   */
  const handleNext = async () => {
    // Validate question type is selected
    if (!questionTypeId) {
      alert(`${t('Please select a valid question type')}`);
      return;
    }

    // Validate no empty questions
    const hasEmptyQuestion = questions.some((q) => q.trim() === '');
    if (hasEmptyQuestion) {
      alert(
        `${'Please complete all questions before saving the questionnaire'}`
      );
      return;
    }

    /**
     * @typedef {Object} QuestionPayload
     * @property {string} text - Question text
     * @property {boolean} invertAnswer - Whether to invert the answer scoring
     * @property {number} questionTypeId - Type of question (scale, yes/no, etc.)
     * @property {number} keyId - Key associated with the question
     */

    /**
     * @typedef {Object} QuestionnairePayload
     * @property {string} name - Questionnaire name
     * @property {string} toBeAppliedAt - When the questionnaire should be applied
     * @property {string} category - Questionnaire category
     * @property {string} correctionType - Algorithm used for scoring
     * @property {string} introduction - Introductory text
     * @property {string} createdBy - User ID of creator
     * @property {Array<QuestionPayload>} questions - List of questions
     */

    /** @type {QuestionnairePayload} */
    const payload = {
      name,
      toBeAppliedAt,
      category,
      correctionType,
      introduction,
      createdBy: userId,
      questions: questions.map((text, index) => ({
        text,
        invertAnswer: selectedQuestions[index],
        questionTypeId: Number(questionTypeId),
        keyId: keyIds[index],
      })),
    };

    // Submit questionnaire and handle response
    const result = await createQuestionnaire(payload);

    if (result) {
      navigate('/questionnaires');
    } else {
      alert(`${t('Error saving questionnaire')}`);
    }
  };

  return (
    <Container>
      <Header>
        <HeaderTitle>
          <BackButton onClick={handleBack}>
            <ChevronLeft size={20} />
          </BackButton>
          <Title>{t('Create Questionnaire')}</Title>
        </HeaderTitle>
        <Button onClick={handleNext} disabled={saving}>
          {saving ? `${t('Saving')}` : `${t('Save')}`}
        </Button>
      </Header>

      <ContentWrapper>
        <FormContainer>
          <FormSection>
            <Input
              placeholder={t('Questionnaire name')}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />

            <SelectContainer>
              <Select
                value={toBeAppliedAt}
                onChange={(e) => setToBeAppliedAt(e.target.value)}
              >
                <option value="" disabled>
                  {t('Time to be applied')}
                </option>
                <option value="inicial">{t('Initial')}</option>
                <option value="diario">{t('Daily')}</option>
                <option value="semanal">{t('Weekly')}</option>
              </Select>
              <SelectArrow>
                <ChevronDown size={16} />
              </SelectArrow>
            </SelectContainer>

            <SelectContainer>
              <Select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                <option value="" disabled>
                  {t('Category')}
                </option>
                <option value="personalidade">{t('Personality')}</option>
                <option value="humor">{t('Mood')}</option>
                <option value="ansiedade">{t('Anxiety')}</option>
                <option value="depressao">{t('Depression')}</option>
                <option value="burnout">{t('Stress')}</option>
                <option value="outros">{t('Other')}</option>
              </Select>
              <SelectArrow>
                <ChevronDown size={16} />
              </SelectArrow>
            </SelectContainer>

            <SelectContainer>
              <Select
                value={questionTypeId}
                onChange={(e) => setQuestionTypeId(e.target.value)}
              >
                <option value="" disabled>
                  {t('Questions type')}
                </option>
                {loading && <option disabled>{t('Loading')}</option>}
                {error && <option disabled>{t('Error loading types')}</option>}
                {questionTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {friendlyLabels[type.identifier] || type.identifier}
                  </option>
                ))}
              </Select>
              <SelectArrow>
                <ChevronDown size={16} />
              </SelectArrow>
            </SelectContainer>

            <SelectContainer>
              <Select
                value={correctionType}
                onChange={(e) => setCorrectionType(e.target.value)}
              >
                <option value="" disabled>
                  {t('Correction type')}
                </option>
                <option value="bigFive">{t('Big Five')}</option>
                <option value="oldenburg">{t('Oldenburg')}</option>
                <option value="equilibrio">{t('Balance Scale')}</option>
              </Select>
              <SelectArrow>
                <ChevronDown size={16} />
              </SelectArrow>
            </SelectContainer>

            <Textarea
              placeholder={t('Initial explanation')}
              value={introduction}
              onChange={(e) => setIntroduction(e.target.value)}
            />
          </FormSection>

          <FormSection>
            <ToggleContainer>
              <ToggleLabel>{t('Invert answer')}</ToggleLabel>
            </ToggleContainer>

            <QuestionsContainer>
              {questions.map((question, index) => (
                <QuestionItem key={index}>
                  <Input
                    placeholder={`${t('Question')} ${index + 1}`}
                    value={question}
                    onChange={(e) =>
                      handleQuestionChange(index, e.target.value)
                    }
                  />

                  {/* Campo de Seleção de Chave */}
                  <SelectContainer>
                    <Select
                      value={keyIds[index]}
                      onChange={(e) => handleKeyChange(index, e.target.value)}
                    >
                      {keysLoading && <option disabled>{t('Loading')}</option>}
                      {keysError && (
                        <option disabled>{t('Error loading keys')}</option>
                      )}
                      {keyOptions.map((opt) => (
                        <option key={opt.id} value={opt.id}>
                          {opt.label}
                        </option>
                      ))}
                    </Select>
                    <SelectArrow>
                      <ChevronDown size={16} />
                    </SelectArrow>
                  </SelectContainer>

                  <Checkbox
                    isChecked={selectedQuestions[index]}
                    onClick={() => toggleQuestion(index)}
                  >
                    {selectedQuestions[index] && '✓'}
                  </Checkbox>

                  {questions.length > 1 && (
                    <button
                      onClick={() => removeQuestion(index)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '0 4px',
                        color: '#e63946',
                      }}
                      title={t('Remove question')}
                    >
                      <X size={16} />
                    </button>
                  )}
                </QuestionItem>
              ))}

              <AddQuestionButton onClick={addQuestion}>
                <Plus size={16} />
              </AddQuestionButton>
            </QuestionsContainer>
          </FormSection>
        </FormContainer>
      </ContentWrapper>
    </Container>
  );
}
